import React, { useState, useEffect, useMemo } from 'react'
import { useEnrollments } from '../../hooks/enrollment'
import { useUsers } from '../../hooks/users'
import { toast } from '../../components/ui/use-toast'
import EnrollmentTabs from './enrollmentTabs'
import { Branch, BranchList } from '@/types/ManageUsers'
import {
  Student,
  Course,
  Subscription,
  InstructorLetTraining,
  CompareUsers,
} from '@/types/EnrollLearners'
import { useAuth } from '@/hooks/useAuth'

type ContentTab = 'Courses' | 'Learning Plans'
interface SelectedSession {
  courseId: string
  sessionId: string
}

const Enroll: React.FC = () => {
  const { loadBranches } = useUsers()
  const {
    SubscriptionsActiveContent,
    fetchSubContentAndStudents,
    fetchSubContentStudents,
    fetchSubCompareUsers,
    enrollSessionsForLearners,
    unEnrollSessionsForLearners,
    enrollCourseStudents,
    enrollLPStudents,
    fetchSubscriptions,
  } = useEnrollments()

  const { currentUser } = useAuth()

  let usrLvl = currentUser.user_level
  const [branchData, setBranchData] = useState<BranchList | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null)

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [selectedIltSubscription, setSelectedIltSubscription] = useState<
    string | null
  >(null)
  const [selectedOnlineSubscription, setSelectedOnlineSubscription] = useState<
    string | null
  >(null)

  const [courses, setCourses] = useState<Course[]>([])
  const [learningPlans, setLearningPlans] = useState<Course[]>([])
  const [instructorLetTraining, setInstructorLetTraining] = useState<
    InstructorLetTraining[]
  >([])

  const [students, setStudents] = useState<Student[]>([])
  const [initialActiveTab, setInitialActiveTab] = useState<
    'instructor' | 'online'
  >('instructor')
  const [selectedSession, setSelectedSession] =
    useState<SelectedSession | null>(null)
  const [enrolledLearners, setEnrolledLearners] = useState<Set<string>>(
    new Set([])
  )

  const [compareUsers, setCompareUsers] = useState<CompareUsers[]>([])
  const [activeTab, setActiveTab] = useState<ContentTab>('Courses')
  const [history, setHistory] = useState<Branch[]>([])

  const [searchQuery, setSearchQuery] = useState<string>('')
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([])
  const [filteredLearningPlans, setFilteredLearningPlans] = useState<Course[]>(
    []
  )

  const [searchQueryStudents, setSearchQueryStudents] = useState<string>('')
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([])

  const [loadingBranches, setLoadingBranches] = useState<boolean>(false)
  const [afterLoading, setAfterLoading] = useState<boolean>(false)
  const [branchBack, setBranchBack] = useState<boolean>(false)

  const [loadingSubscriptions, setLoadingSubscriptions] =
    useState<boolean>(false)
  const [loadingContent, setLoadingContent] = useState<boolean>(false)
  const [loadingStudentsContent, setLoadingStudentsContent] =
    useState<boolean>(false)
  const [enrollingLearners, setEnrollingLearners] = useState<Set<string>>(
    new Set()
  )

  const normId = (v: unknown) => (v == null ? '' : String(v))

  useEffect(() => {
    setLoadingBranches(true)
    setLoadingContent(true)
    setInstructorLetTraining([])
    loadBranches().then((data) => {
      setBranchData(data)
      setSelectedBranch(data.branch)
      setLoadingBranches(false)
      setLoadingContent(false)
      setAfterLoading(true)
    })

    if (afterLoading) {
      setLoadingSubscriptions(true)
      fetchSubscriptions().then((data) => {
        const normalized = (data || []).map((s: any) => ({
          ...s,
          id: normId(s.id),
        }))
        setSubscriptions(normalized)
        setLoadingSubscriptions(false)
        setAfterLoading(false)
      })
    }
  }, [])

  useEffect(() => {
    // When Unselected Disable enroll for learners
    if (selectedSession?.courseId !== undefined) {
      getSelectedSessionLearners()
    }
    else {
      setCompareUsers([])
    }
  }, [selectedSession])

  const getSelectedSessionLearners = () => {
    setLoadingStudentsContent(true)

    const selectedSubscription =
      initialActiveTab === 'instructor'
        ? selectedIltSubscription
        : selectedOnlineSubscription

    // branch ID Need
    if (selectedSession?.courseId && selectedSession?.sessionId) {
      fetchSubCompareUsers(
        selectedSession?.courseId,
        selectedSession?.sessionId,
        selectedBranch.id || ''
      ).then((data) => {
        const normalizedCompareUsers = (data.data || []).map((u: any) => ({
          ...u,
          user_id: normId(u.user_id),
        }))

        setLoadingStudentsContent(false)
        setCompareUsers(normalizedCompareUsers)
      })

      if(usrLvl != 6){
        fetchSubContentStudents(selectedSubscription).then(({ students }) => {
          const normalizedStudents = (students || []).map((s: any) => ({
            ...s,
            id: normId(s.id),
          }))
          setStudents(normalizedStudents)
        })
      }
    }
  }

  useEffect(() => {
    if (selectedBranch && afterLoading && branchBack == false) {
      setLoadingSubscriptions(true)
      fetchSubscriptions(selectedBranch.id).then((data) => {
        const normalized = (data || []).map((s: any) => ({
          ...s,
          id: normId(s.id),
        }))
        setSubscriptions(normalized)
        setLoadingSubscriptions(false)
      })
    }
  }, [selectedBranch])

  useEffect(() => {
    if (selectedIltSubscription) {
      const exists = subscriptions.some((s) => s.id === selectedIltSubscription)
      if (!exists) {
        setSelectedIltSubscription(null)
      }
    }
    if (selectedOnlineSubscription) {
      const exists = subscriptions.some(
        (s) => s.id === selectedOnlineSubscription
      )
      if (!exists) {
        setSelectedOnlineSubscription(null)
      }
    }
  }, [selectedIltSubscription, selectedOnlineSubscription])

  useEffect(() => {
    const selectedSubscription =
      initialActiveTab === 'instructor'
        ? selectedIltSubscription
        : selectedOnlineSubscription

    if (selectedSubscription) {
      setLoadingStudentsContent(true)
      if(usrLvl != 6){
        fetchSubContentStudents(selectedSubscription).then(({ students }) => {
          const normalizedStudents = (students || []).map((s: any) => ({
            ...s,
            id: normId(s.id),
          }))
          setStudents(normalizedStudents)
          setLoadingStudentsContent(false)
        })
      }
    } else {
      setLoadingStudentsContent(false)
      setStudents([])
      setFilteredStudents([])
    }
  }, [selectedIltSubscription, selectedOnlineSubscription])

  useEffect(() => {
    const selectedSubscription =
      initialActiveTab === 'instructor'
        ? selectedIltSubscription
        : selectedOnlineSubscription
    setStudents([])
    setCompareUsers([])
    setFilteredStudents([])
    setSelectedSession(null)

    if (selectedSubscription) {
      setLoadingContent(true)

      const subscriptionObj = subscriptions.find(
        (s) => s.id === selectedSubscription
      )

      if (
        subscriptionObj &&
        subscriptionObj.catalog_ids &&
        subscriptionObj.catalog_ids.length > 0
      ) {
        const catalogId = subscriptionObj?.catalog_ids?.map(id => Number(id)) || [];

        // Load content based on active tab
        if (initialActiveTab === 'instructor') {
          // Instructor-led Training API
          SubscriptionsActiveContent(catalogId)
            .then(({ courses }) => {
              setInstructorLetTraining(courses)
              setLoadingContent(false)
            })
            .catch((err) => {
              console.error('Error loading instructor content:', err)
              setLoadingContent(false)
            })
        } else {
          // Online training API
          fetchSubContentAndStudents(selectedSubscription)
            .then(({ courses }) => {
              const learningPlans = courses.filter(
                (c: Course) => c.item_type === 'learning_plan'
              )
              const onlyCourses = courses.filter(
                (c: Course) => c.item_type === 'course'
              )

              setCourses(onlyCourses)
              setLearningPlans(learningPlans)
              setFilteredCourses(onlyCourses)
              setFilteredLearningPlans(learningPlans)

              setActiveTab(
                onlyCourses.length > 0
                  ? 'Courses'
                  : learningPlans.length > 0
                    ? 'Learning Plans'
                    : 'Courses'
              )
              setLoadingContent(false)
            })
            .catch((err) => {
              console.error('Error loading online content:', err)
              setLoadingContent(false)
            })
        }
      } else {
        setLoadingContent(false)
        setCourses([])
        setLearningPlans([])
        setFilteredCourses([])
        setFilteredLearningPlans([])
      }
    } else {
      setLoadingContent(false)
      setCourses([])
      setLearningPlans([])
      setFilteredCourses([])
      setFilteredLearningPlans([])
      setFilteredStudents([])
    }
  }, [
    selectedIltSubscription,
    selectedOnlineSubscription,
    // initialActiveTab,
    subscriptions,
  ])

  useEffect(() => {
    setLoadingContent(true)
    const q = searchQuery.toLowerCase()
    setFilteredCourses(
      courses.filter(
        (c) =>
          c.item_name.toLowerCase().includes(q) ||
          c.item_code.toLowerCase().includes(q)
      )
    )
    setFilteredLearningPlans(
      learningPlans.filter(
        (lp) =>
          lp.item_name.toLowerCase().includes(q) ||
          lp.item_code.toLowerCase().includes(q)
      )
    )
    setLoadingContent(false)
  }, [searchQuery, courses, learningPlans])

  useEffect(() => {
    const q = searchQueryStudents.toLowerCase()
    setFilteredStudents(
      students.filter(
        (s) =>
          s.first_name.toLowerCase().includes(q) ||
          s.last_name.toLowerCase().includes(q)
      )
    )
  }, [searchQueryStudents, students])

  const handleBranchSelect = (branch: Branch) => {
    if (selectedBranch) setHistory((prev) => [...prev, selectedBranch])
    setLoadingBranches(true)
    setLoadingSubscriptions(true)
    setLoadingContent(true)
    setSelectedIltSubscription(null)
    setSelectedOnlineSubscription(null)
    setInstructorLetTraining([])
    setSubscriptions([])
    loadBranches(branch.id).then((data) => {
      setBranchData(data)
      setSelectedBranch(data.branch)
      setLoadingBranches(false)
      setLoadingSubscriptions(false)
      setLoadingContent(false)
    })
  }

  const handleBack = () => {
    if (history.length === 0) return
    setLoadingBranches(true)
    setBranchBack(true)
    setSelectedIltSubscription(null)
    setSelectedOnlineSubscription(null)
    handleBranchBack()
  }

  //single branch back option
  const handleBranchBack = () => {
    setLoadingContent(true)
    setInstructorLetTraining([])
    setSubscriptions([])
    if (history.length === 0) return
    const previousBranch = history[history.length - 1]
    setHistory((prev) => prev.slice(0, -1))

    setSelectedBranch(previousBranch)
    loadBranches(previousBranch.id).then((data) => {
      setBranchData(data)
      setLoadingBranches(false)

      setLoadingSubscriptions(true)
      fetchSubscriptions(previousBranch.id).then((data) => {
        const normalized = (data || []).map((s: any) => ({
          ...s,
          id: normId(s.id),
        }))
        setSubscriptions(normalized)
        setLoadingSubscriptions(false)
      })
      setAfterLoading(true)
      setBranchBack(false)
      setLoadingContent(false)
    })
  }

  const pickIltSubscription = (id: string) => {
    const normalized = id ? normId(id) : null
    setSelectedIltSubscription(normalized)
  }

  const pickOnlineSubscription = (id: string) => {
    const normalized = id ? normId(id) : null
    setSelectedOnlineSubscription(normalized)
  }

  // Instructor-Led Training Enroll
  const handleEnrollLearner = async (learnerId: string) => {
    if (!selectedSession) {
      toast({
        description: 'Please select a training session first.',
        variant: 'destructive',
      })
      return
    }

    setEnrollingLearners((prev) => new Set([...prev, learnerId]))

    try {
      const transactionCode = subscriptions.find(
        (s) => s.id === selectedIltSubscription
      )?.transaction_code

      const response = await enrollSessionsForLearners(
        selectedSession?.courseId,
        selectedSession?.sessionId,
        [learnerId],
        transactionCode
      )
      if (!response.success) {
        toast({
          title: 'Already Enrolled',
          description: response.errors[0],
          variant: 'destructive',
        })
        return
      } else {
        setEnrolledLearners((prev) => new Set([...prev, learnerId]))

        setCompareUsers((prevUsers) => {
          const existingUser = prevUsers.find(
            (u) => String(u.user_id) === String(learnerId)
          )

          if (existingUser) {
            // Update existing user's status
            return prevUsers.map((u) =>
              String(u.user_id) === String(learnerId)
                ? { ...u, status: 'enrolled' }
                : u
            )
          } else {
            // Add new user to the list
            const learner = students.find(
              (s) => String(s.id) === String(learnerId)
            )
            if (learner) {
              return [
                ...prevUsers,
                {
                  user_id: learnerId,
                  status: 'enrolled',
                  first_name: learner.first_name,
                  last_name: learner.last_name,
                },
              ]
            }
          }
          return prevUsers
        })
        toast({
          title: 'Success!',
          description: 'Learner successfully enrolled.',
        })
      }
    } catch (err) {
      console.error('Enrollment failed:', err)
      toast({
        title: 'Uh Oh!',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setEnrollingLearners((prev) => {
        const newSet = new Set(prev)
        newSet.delete(learnerId)
        return newSet
      })
    }
  }

  // Instructor-Led Training Unenroll
  const handleUnenrollLearner = async (learnerId: string) => {
    if (!selectedSession) {
      toast({
        description: 'Please select a training session first.',
        variant: 'destructive',
      })
      return
    }
    setEnrollingLearners((prev) => new Set([...prev, learnerId]))

    try {
      const transactionCode = subscriptions.find(
        (s) => s.id === selectedIltSubscription
      )?.transaction_code

      const response = await unEnrollSessionsForLearners(
        selectedSession?.courseId,
        selectedSession?.sessionId,
        learnerId,
        transactionCode
      )
      if (!response.success) {
        toast({
          title: 'Unenrollment Failed',
          description: response.errors[0],
          variant: 'destructive',
        })
        return
      } else {
        setCompareUsers((prevUsers) =>
          prevUsers.map((u) =>
            String(u.user_id) === String(learnerId) ? { ...u, status: '' } : u
          )
        )
        toast({
          title: 'Success!',
          description: 'Learner successfully unenrolled.',
        })
      }
    } catch (err) {
      console.error('Enrollment failed:', err)
      toast({
        title: 'Uh Oh!',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setEnrollingLearners((prev) => {
        const newSet = new Set(prev)
        newSet.delete(learnerId)
        return newSet
      })
    }
  }

  // Handle Online Training enrollment
  const handleOnlineEnroll = async (
    contentIds: string[],
    learnerIds: string[],
    contentType: 'course' | 'learning_plan'
  ) => {
    const selectedSubscription =
      initialActiveTab === 'instructor'
        ? selectedIltSubscription
        : selectedOnlineSubscription

    try {
      const enrollFn =
        contentType === 'course' ? enrollCourseStudents : enrollLPStudents

      const response = await enrollFn(
        selectedSubscription,
        contentIds,
        learnerIds
      )

      // Parse enrolled and existing
      const enrolledList = response.enrolled || [];
      const existingList = response.errors?.existing_enrollments || [];

      // Count unique enrolled users/courses
      const enrolledUsers = [...new Set(enrolledList.map((e: any) => e.id_user))].length;
      const enrolledCourses = [...new Set(enrolledList.map((e: any) => e.id_course))].length;

      // Count unique existing
      const existingUsers = [...new Set(existingList.map((e: any) => e.user_id))].length;
      const existingCourses = [...new Set(existingList.map((e: any) => e.course_id))].length;

      // Build lookup maps so toast shows user name + course name
      const studentById = new Map((students || []).map((s: any) => [String(s.id), s]))
      const courseById = new Map((courses || []).map((c: any) => [String(c.item_id ?? c.id), c]))
      const learningPlanById = new Map(
        (learningPlans || []).map((lp: any) => [String(lp.item_id ?? lp.id), lp])
      )

      const getUserLabel = (id: any) => {
        const key = String(id)
        const s = studentById.get(key)
        if (s) return `${s.first_name} ${s.last_name}`.trim()
        return `User ${id}`
      }

      const getCourseLabel = (id: any) => {
        const key = String(id)
        const course = courseById.get(key) || learningPlanById.get(key)
        if (course) return course.item_name || course.name || `Course ${id}`
        return `Course ${id}`
      }

      const enrolledDetails = enrolledList
        .map((e: any) => `${getUserLabel(e.id_user)} -> ${getCourseLabel(e.id_course)}`)
        .join(', ')

      // Success toast for new enrollments
      if (enrolledList.length > 0) {
        toast({
          title: 'Enrolled Successfully!',
          description: `Enrolled ${enrolledUsers} user(s) in ${enrolledCourses} course(s): ${enrolledDetails}`,
        })
      } else {
        toast({
          title: 'No New Enrollments',
          description: `All selected users were already enrolled in the selected courses.`,
        })
      }

      // Warning toast for existing (already enrolled) might use later
      // if (existingList.length > 0) {
      //   toast({
      //     title: 'Already Enrolled',
      //     description: `Skipped: ${existingUsers} user(s) in ${existingCourses} course(s): ${existingDetails}`,
      //     variant: 'destructive',
      //   });
      // }

      return response
    } catch (err) {
      toast({
        title: 'Uh Oh!',
        description: 'An unexpected error occurred.',
        variant: 'destructive',
      })
      throw err
    }
  }

  return (
    <div className="flex flex-col lg:flex-row lg:container gap-4 items-stretch w-full">
      {/* Hide Branch Navigation for Technician */}
      { !(currentUser.user_level >= 6) && (
        <div className="w-full lg:w-2/6 flex-grow">
          <div className="p-4 rounded border-[2px] border-gray-300 bg-white h-full">
            <div className="flex justify-between items-start">
              <h2 className="font-medium text-gray-900 mb-4">
                Branch Navigation
              </h2>
              {history.length > 0 && (
                <button
                  onClick={handleBack}
                  className="text-primary hover:underline cursor-pointer"
                >
                  &larr; Back
                </button>
              )}
            </div>

            {loadingBranches || loadingSubscriptions ? (
              <div className="flex flex-col items-center justify-center w-full h-[500px]">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent mb-4"></div>
                <p className="text-lg font-semibold text-gray-800">
                  Loading branches...
                </p>
              </div>
            ) : branchData ? (
              <>
                <button
                  className={`p-2 text-left w-full ${selectedBranch?.id === branchData.branch.id ? 'bg-primary rounded text-white' : ''}`}
                >
                  {branchData.branch.name}
                </button>
                <div className="mt-2 pl-4 flex flex-col gap-1 overflow-auto max-h-[500px]">
                  {branchData.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleBranchSelect(child)}
                      className={`p-2 text-left font-normal w-full rounded border-[3px] border-primary hover:bg-gray-100 ${selectedBranch?.id === child.id ? 'bg-primary text-white' : ''}`}
                    >
                      {child.name}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p>No branch data available.</p>
            )}
          </div>
        </div>
      )}

      <>
        <EnrollmentTabs
          activeTab={initialActiveTab}
          onTabChange={setInitialActiveTab}
          loadingBranches={loadingBranches}
          loadingSubscriptions={loadingSubscriptions}
          loadingContent={loadingContent}
          setloadingContent={setLoadingContent}
          subscriptions={subscriptions}
          selectedSubscription={
            initialActiveTab === 'instructor'
              ? selectedIltSubscription
              : selectedOnlineSubscription
          }
          onSubscriptionChange={
            initialActiveTab === 'instructor'
              ? pickIltSubscription
              : pickOnlineSubscription
          }
          searchSessions={searchQuery}
          onSearchSessionsChange={setSearchQuery}
          sessions={
            initialActiveTab === 'instructor'
              ? filteredCourses
              : filteredLearningPlans
          }
          selectedSession={selectedSession}
          onSessionSelect={setSelectedSession}
          learners={filteredStudents}
          loadingStudentsContent={loadingStudentsContent}
          compareUsers={compareUsers}
          searchLearners={searchQueryStudents}
          onSearchLearnersChange={setSearchQueryStudents}
          enrolledLearners={enrolledLearners}
          enrollingLearners={enrollingLearners}
          onEnroll={handleEnrollLearner}
          onUnenroll={handleUnenrollLearner}
          instructorLetTraining={instructorLetTraining}
          courses={courses}
          learningPlans={learningPlans}
          onEnrollOnline={handleOnlineEnroll}
        />
      </>
    </div>
  )
}

export default Enroll